import type {
  CourseHomePerspective,
  LearningCourseContext, TeachingCourseContext,
  CourseHomeProjectionV6,
  LearningCourseHomeProjectionV6,
  TeachingCourseHomeProjectionV6,
} from "./courseHome";
import type { ClassroomExactRecord } from "./classroom";

type Check = (value: unknown) => boolean;

const string: Check = (value) => typeof value === "string";
const boolean: Check = (value) => typeof value === "boolean";
const integer: Check = (value) => typeof value === "number" && Number.isInteger(value);
const number: Check = (value) => typeof value === "number" && Number.isFinite(value);
const count: Check = (value) => integer(value) && Number(value) >= 0;
const positive: Check = (value) => integer(value) && Number(value) >= 1;
const href: Check = (value) => typeof value === "string" && value.startsWith("/");
const oneOf = (...values: string[]): Check => (value) => values.some((item) => item === value);
const nullable = (check: Check): Check => (value) => value === null || check(value);
const either = (...checks: Check[]): Check => (value) => checks.some((check) => check(value));
const list = (check: Check, min = 0, max = Infinity): Check => (value) =>
  Array.isArray(value) && value.length >= min && value.length <= max && value.every(check);

function closed(fields: Record<string, Check>, optional: Record<string, Check> = {}): Check {
  const keys = Object.keys(fields);
  return (value) => {
    if (value === null || typeof value !== "object" || Array.isArray(value)) return false;
    const record = value as Record<string, unknown>;
    return Object.keys(record).every(key => key in fields || (key in optional && optional[key](record[key])))
      && keys.every((key) => Object.hasOwn(record, key) && fields[key](record[key]));
  };
}

const empty = closed({ state: oneOf("empty") });
const unavailable = closed({ state: oneOf("unavailable") });
const section = (value: Check): Check => either(closed({ state: oneOf("ready"), value }), empty, unavailable);
const action = (id: string, label: string): Check => closed({ id: oneOf(id), label: oneOf(label), href });
const openSession = action("open_session", "Open session");
const openAssignment = action("open_assignment", "Open assignment");
const reviewAttempt = action("review_attempt", "Review attempt");
const unit = closed({ id: string, position: integer, topic: string, starts_at: nullable(string) });
const sequence = closed({ phase: oneOf("before_course", "in_progress", "after_course"), current: nullable(unit), next: nullable(unit) });
const learnerState = oneOf("unattempted", "submitted", "resubmitted", "returned", "graded");

const courseShape = (status: Check) => closed({
  id: string, code: string, title: string, title_ar: nullable(string), crn: nullable(string),
  status, timezone: string,
  program: nullable(closed({ id: string, name: string })),
  term: nullable(closed({
    id: string, name: string, status: oneOf("active", "upcoming", "closed"),
    starts_on: nullable(string), ends_on: nullable(string),
  })),
});
const course = courseShape(oneOf("draft", "published"));
const recordCourse = courseShape(oneOf("draft", "published", "archived"));


const occurrence = closed({
  row_id: string, id: string, label: nullable(string), starts_at: string, ends_at: string, timezone: string,
  institutional_status: oneOf("scheduled", "held", "cancelled"),
  clock_phase: oneOf("upcoming", "scheduled_now", "status_pending"),
  modality: oneOf("online", "in_person", "hybrid"), location: nullable(string),
  readiness: oneOf("ready", "missing", "invalid", "in_person", "cancelled", "held"),
  destination: openSession,
});
const courseworkFields = {
  row_id: string, assignment_id: string, title: string, due_at: nullable(string), points: nullable(string),
};
// Recorded session-plan placement; null is the whole course. Only the teaching team reads the plan's topic.
const placeOf = (title: Check) => nullable(closed({ unit_id: string }, { unit_title: title, session_id: nullable(string), session_no: nullable(integer) }));
const teachingPlace = placeOf(nullable(string));
const learningPlace = placeOf((value) => value === null);
const quizFields = { quiz_mode: nullable(oneOf("graded", "practice")), quiz_result_mode: nullable(oneOf("graded", "practice")), work_required: boolean, learner_act: nullable(oneOf("hand_in", "mark_done", "mark_taken")) };
const teachingCourseworkRow = closed({
  ...courseworkFields, status: oneOf("draft", "published", "closed"),
  coverage: closed({ enrolled_count: count, submitted_count: count, awaiting_review_count: count }, { awaiting_release_count: count }),
  destination: either(openAssignment, reviewAttempt),
}, { archived_at: nullable(string), place: teachingPlace, ...quizFields });
const learningCourseworkRow = closed({
  ...courseworkFields, status: oneOf("published", "closed"), learner_state: learnerState,
  receipt: nullable(closed({ attempt_number: positive, submitted_at: string })),
  feedback_released_at: nullable(string), resubmission_open: boolean,
  destination: either(openAssignment, action("view_receipt", "View receipt"), action("view_feedback", "View feedback")),
}, { place: learningPlace, ...quizFields });

const classroomObservation = closed({
  state: oneOf("ready", "empty", "unavailable"),
  last_successful_at: nullable(string), unavailable_at: nullable(string),
});
const classroomSubmissionDestination = either(
  action("open_classroom_submission", "Open record"),
  action("open_classroom_submission", "Open your record"),
);
const classroomRecovery = nullable(closed({
  actor: oneOf("instructor", "learner", "administrator"), reason: string,
  destination: nullable(either(
    action("open_course_connections", "Open course connections"),
    action("open_classroom_recovery", "Open Classroom recovery"),
    classroomSubmissionDestination,
  )),
}));
const classroomSubmission = closed({
  institutional_id: string, provider_submission_id: string, enrollment_id: string,
  learner_display_name: nullable(string),
  enrollment_status: oneOf("enrolled", "withdrawn", "incomplete", "complete"),
  state: oneOf("NEW", "CREATED", "TURNED_IN", "RETURNED", "RECLAIMED_BY_STUDENT"),
  copy_state: oneOf("ready", "delayed_copy", "not_observed", "unavailable"),
  provider_url: nullable(string), copy_url: nullable(string), copy_title: nullable(string),
  draft_grade: nullable(number), assigned_grade: nullable(number), last_successful_at: nullable(string),
  unavailable_at: nullable(string), submitted_at: nullable(string),
  destination: classroomSubmissionDestination,
  permitted_actions: list(oneOf(
    "open", "submission_turn_in", "submission_reclaim", "grade_patch", "submission_return", "result_proposal",
  )),
}, { history: list(closed({ state: oneOf("TURNED_IN", "RETURNED", "RECLAIMED_BY_STUDENT"), at: string })) });
const classroomCourseworkFields = {
  owner: oneOf("classroom"), row_id: string, coursework_id: string, provider_coursework_id: string,
  provenance: oneOf("app_created", "provider_discovered"), provider_write_eligible: boolean,
  title: string, instructions: nullable(string), status: oneOf("DRAFT", "PUBLISHED"), due_at: nullable(string), points: nullable(number),
  provider_url: nullable(string), last_successful_at: nullable(string), unavailable_at: nullable(string),
  coverage: closed({ recorded_count: count, submitted_count: count }),
  destination: either(
    action("open_classroom_coursework", "Open record"),
    classroomSubmissionDestination,
  ),
  permitted_actions: list(oneOf("open")),
};
const classroomFields = {
  enabled: (value: unknown) => value === true, owner: oneOf("classroom"), account_label: nullable(string),
  course_name: nullable(string), observation: classroomObservation, recovery: classroomRecovery,
};
const classroomDisabled = closed({ enabled: (value) => value === false });
const teachingClassroomCoursework = closed({
  ...classroomCourseworkFields,
  audience: closed({ mode: oneOf("course_wide", "individual_students"), learner_count: nullable(count) }),
  submissions: list(classroomSubmission),
}, { materials: list(closed({ title: string, url: string })), posted_read_at: nullable(string) });
const learningClassroomCoursework = closed({
  ...classroomCourseworkFields,
  audience: closed({ mode: oneOf("course_wide", "addressed_to_you", "own_record") }),
  submission: nullable(classroomSubmission),
}, { materials: list(closed({ title: string, url: string })), posted_read_at: nullable(string) });
const teachingClassroom = either(classroomDisabled, closed({
  ...classroomFields,
  // Option C retires creating Classroom work from IMAM: the server never sends it.
  create_destination: (value) => value === null,
  items: list(teachingClassroomCoursework),
}));
const learningClassroom = either(classroomDisabled, closed({
  ...classroomFields, create_destination: (value) => value === null,
  items: list(learningClassroomCoursework),
}));

const recordingPolicy = closed({
  version: string,
  standing_notice: string,
  class_notice: string,
  support_email: string,
});

const commonFields = {
  projection_version: oneOf("course-home-v6"), generated_at: string, course,
  recording_policy: nullable(recordingPolicy),
  session: section(occurrence), sequence: section(sequence),
};
const teaching = closed({
  ...commonFields, perspective: oneOf("teaching"),
  relationship: closed({ kind: oneOf("primary", "co_instructor"), teaching_assignment_id: string }),
  coursework: section(closed({ items: list(teachingCourseworkRow, 1) })),
  classroom: teachingClassroom,
});
const learning = closed({
  ...commonFields, perspective: oneOf("learning"),
  relationship: closed({ kind: oneOf("enrollment"), enrollment_id: string, participation_kind: oneOf("credit", "audit") }),
  coursework: section(closed({ items: list(learningCourseworkRow, 1) })),
  classroom: learningClassroom,
});

export function decodeCourseHomeProjectionV6(value: unknown, perspective: "teaching"): TeachingCourseHomeProjectionV6;
export function decodeCourseHomeProjectionV6(value: unknown, perspective: "learning"): LearningCourseHomeProjectionV6;
export function decodeCourseHomeProjectionV6(value: unknown, perspective?: CourseHomePerspective): CourseHomeProjectionV6;
/** Validates the closed v6 body without transforming its server-owned rows or destinations. */
export function decodeCourseHomeProjectionV6(value: unknown, perspective?: CourseHomePerspective): CourseHomeProjectionV6 {
  const valid = perspective === "teaching" ? teaching(value)
    : perspective === "learning" ? learning(value)
      : teaching(value) || learning(value);
  if (!valid) throw new Error("Course home v6 response is invalid.");
  return value as CourseHomeProjectionV6;
}

const exactTeachingClassroomRecord = closed({
  observation: classroomObservation,
  recovery: classroomRecovery,
  coursework: teachingClassroomCoursework,
  submission: nullable(classroomSubmission),
});
const exactLearningClassroomRecord = closed({
  observation: classroomObservation,
  recovery: classroomRecovery,
  coursework: learningClassroomCoursework,
  submission: nullable(classroomSubmission),
});

export function decodeClassroomExactRecord(value: unknown): ClassroomExactRecord {
  if (!exactTeachingClassroomRecord(value) && !exactLearningClassroomRecord(value)) {
    throw new Error("Classroom exact record response is invalid.");
  }
  const exact = value as ClassroomExactRecord;
  if (exact.submission !== null) {
    const candidates = "submissions" in exact.coursework
      ? exact.coursework.submissions
      : exact.coursework.submission === null ? [] : [exact.coursework.submission];
    if (!candidates.some((item) => item.institutional_id === exact.submission?.institutional_id)) {
      throw new Error("Classroom exact record response is invalid.");
    }
  }
  return exact;
}


/** The exact record context has no optional whole-course sections. */
export function decodeLearningCourseContext(value: unknown, courseId: string): LearningCourseContext {
  const shape = closed({ course: recordCourse, relationship: closed({ kind: oneOf("enrollment"), enrollment_id: string, participation_kind: oneOf("credit", "audit") }, { enrollment_status: oneOf("enrolled", "incomplete", "complete") }) });
  if (!shape(value) || (value as LearningCourseContext).course.id !== courseId) throw new Error("This learner course context could not be read.");
  return value as LearningCourseContext;
}

export function decodeTeachingCourseContext(value: unknown, courseId: string): TeachingCourseContext {
  const shape = closed({ course: recordCourse, relationship: closed({ kind: oneOf("primary", "co_instructor"), teaching_assignment_id: string }) });
  if (!shape(value) || (value as TeachingCourseContext).course.id !== courseId) throw new Error("This teaching course context could not be read.");
  return value as TeachingCourseContext;
}
